import { useRef, useState, type ChangeEvent } from 'react';
import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';
import { readableTextOn } from '@/utils/branding';
import type { LogoAsset } from '@/types/portal';

interface LogoAssetsPanelProps {
  initials: string;
  primary: string;
  assets: LogoAsset[];
  /** Uploaded logos keyed by asset id (data URLs). */
  logos: Record<string, string>;
  canEdit: boolean;
  onUpload: (id: string, dataUrl: string) => void;
  onRemove: (id: string) => void;
}

const MAX_BYTES = 2 * 1024 * 1024;

export default function LogoAssetsPanel({
  initials,
  primary,
  assets,
  logos,
  canEdit,
  onUpload,
  onRemove,
}: LogoAssetsPanelProps) {
  const onPrimary = readableTextOn(primary);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const pick = (id: string) => {
    if (!canEdit) return;
    setError('');
    setPendingId(id);
    inputRef.current?.click();
  };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const targetId = pendingId;
    setPendingId(null);
    if (!file || !targetId) return;

    if (!file.type.startsWith('image/')) {
      setError('That file is not an image. Use a PNG, JPG, WebP or SVG.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Images must be under 2 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        onUpload(targetId, reader.result);
        setError('');
      }
    };
    reader.onerror = () => setError('We could not read that file — try another image.');
    reader.readAsDataURL(file);
  };

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={handleFile}
        aria-hidden="true"
        tabIndex={-1}
      />

      <header className="flex items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">Logo assets</h3>
          <p className="mt-0.5 text-xs text-foreground-500">
            {assets.length} files power the portal
          </p>
        </div>
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-base text-secondary-800">
          <i className="ri-image-line" />
        </span>
      </header>

      <ul className="divide-y divide-background-100">
        {assets.map((asset) => {
          const uploaded = logos[asset.id];
          const isPending = pendingId === asset.id;
          return (
            <li key={asset.id} className="flex items-center gap-3 px-5 py-3">
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-300 bg-background-50 text-sm font-bold"
                style={uploaded ? undefined : { backgroundColor: primary, color: onPrimary }}
              >
                {uploaded ? (
                  <img
                    src={uploaded}
                    alt={`${asset.name} preview`}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  initials || '··'
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground-900">{asset.name}</p>
                <p className="truncate text-[11px] text-foreground-500">{asset.description}</p>
                <p className="mt-0.5 flex items-center gap-1 text-[10px] text-foreground-400">
                  {uploaded ? (
                    <span className="inline-flex items-center gap-1 text-accent-700">
                      <i className="ri-checkbox-circle-line" />
                      Custom upload active
                    </span>
                  ) : (
                    <span>
                      {asset.dimensions} · {asset.kind}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {uploaded ? (
                  <button
                    type="button"
                    disabled={!canEdit}
                    onClick={() => onRemove(asset.id)}
                    aria-label={`Remove ${asset.name}`}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-500 cursor-pointer hover:bg-background-100 disabled:cursor-not-allowed"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  icon={isPending ? 'ri-loader-4-line' : 'ri-upload-2-line'}
                  disabled={!canEdit}
                  onClick={() => pick(asset.id)}
                >
                  {uploaded ? 'Replace' : 'Upload'}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="border-t border-background-100 px-5 py-3">
        {error ? (
          <p className="mb-2 inline-flex items-center gap-1.5 text-[11px] text-primary-700">
            <i className="ri-error-warning-line" />
            {error}
          </p>
        ) : null}
        <Badge tone="outline" icon="ri-information-line">
          SVG or transparent PNG preferred · max 2 MB
        </Badge>
      </div>
    </section>
  );
}